// src/errors.ts
var ApiClientError = class extends Error {
  code;
  statusCode;
  details;
  constructor(message, code, statusCode, details) {
    super(message);
    this.name = "ApiClientError";
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      details: this.details
    };
  }
};
var NetworkError = class extends Error {
  constructor(message = "Network error occurred") {
    super(message);
    this.name = "NetworkError";
  }
};
var UnauthorizedError = class extends ApiClientError {
  constructor(message = "Unauthorized") {
    super(message, "UNAUTHORIZED", 401);
    this.name = "UnauthorizedError";
  }
};
var NotFoundError = class extends ApiClientError {
  constructor(message = "Resource not found") {
    super(message, "NOT_FOUND", 404);
    this.name = "NotFoundError";
  }
};
var ValidationError = class extends ApiClientError {
  constructor(message = "Validation failed", details) {
    super(message, "VALIDATION_ERROR", 400, details);
    this.name = "ValidationError";
  }
};
var RateLimitError = class extends ApiClientError {
  retryAfter;
  constructor(message = "Rate limit exceeded", retryAfter) {
    super(message, "RATE_LIMITED", 429, { retryAfter });
    this.name = "RateLimitError";
    this.retryAfter = retryAfter;
  }
};
function isApiClientError(error) {
  return error instanceof ApiClientError;
}
function isNetworkError(error) {
  return error instanceof NetworkError;
}
function isUnauthorizedError(error) {
  return error instanceof UnauthorizedError;
}
function getErrorMessage(error) {
  if (isNetworkError(error)) {
    return "Unable to connect. Please check your internet connection.";
  }
  if (isUnauthorizedError(error)) {
    return "Your session has expired. Please sign in again.";
  }
  if (isApiClientError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred. Please try again.";
}

// src/client.ts
var ApiClient = class {
  config;
  constructor(config) {
    this.config = config;
  }
  getBaseUrl() {
    return this.config.baseUrl;
  }
  buildUrl(path, params) {
    const url = new URL(path, this.config.baseUrl);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== void 0) {
          url.searchParams.set(key, String(value));
        }
      });
    }
    return url.toString();
  }
  async buildHeaders(customHeaders) {
    const headers = new Headers({
      "Content-Type": "application/json",
      ...this.config.headers,
      ...customHeaders
    });
    const token = await this.config.getAccessToken?.();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  }
  async request(options) {
    const { method, path, body, params, headers: customHeaders } = options;
    const url = this.buildUrl(path, params);
    const headers = await this.buildHeaders(customHeaders);
    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : void 0
      });
      if (response.status === 401) {
        this.config.onUnauthorized?.();
        throw new UnauthorizedError("Session expired. Please sign in again.");
      }
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new ApiClientError(
          data.error?.message ?? "An error occurred",
          data.error?.code ?? "INTERNAL_ERROR",
          response.status,
          data.error?.details
        );
      }
      return data.data;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw error;
      }
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new NetworkError(
          "Unable to connect to the server. Please check your internet connection."
        );
      }
      throw error;
    }
  }
  // Convenience methods
  async get(path, params) {
    return this.request({ method: "GET", path, params });
  }
  async post(path, body) {
    return this.request({ method: "POST", path, body });
  }
  async put(path, body) {
    return this.request({ method: "PUT", path, body });
  }
  async patch(path, body) {
    return this.request({ method: "PATCH", path, body });
  }
  async delete(path) {
    return this.request({ method: "DELETE", path });
  }
};
function createApiClient(config) {
  return new ApiClient(config);
}

// src/endpoints.ts
function createHealthEndpoints(client) {
  return {
    check: () => client.get("/health")
  };
}
function createHelloEndpoints(client) {
  return {
    get: (lang) => client.get("/hello", lang ? { lang } : void 0)
  };
}
function createUploadEndpoints(client) {
  return {
    // Note: File upload requires FormData, not JSON
    // This is a placeholder - actual upload needs different handling
    upload: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${client.getBaseUrl()}/upload`, {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error?.message ?? "Upload failed");
      }
      return data.data;
    }
  };
}
function createApiEndpoints(client) {
  return {
    health: createHealthEndpoints(client),
    hello: createHelloEndpoints(client),
    uploads: createUploadEndpoints(client)
  };
}

export { ApiClient, ApiClientError, NetworkError, NotFoundError, RateLimitError, UnauthorizedError, ValidationError, createApiClient, createApiEndpoints, createHealthEndpoints, createHelloEndpoints, createUploadEndpoints, getErrorMessage, isApiClientError, isNetworkError, isUnauthorizedError };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map